import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { getEmptyFilterWithValues } from '../../interfaces/ProductFilter/FilterWithValues';
import {
  createFilterKey,
  deleteFilterKey,
  getFilterWithValues,
  updateFilterKey,
} from '../../api/ProductFilter';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import { FilterValueChip } from './FilterValueChip';
import { UpsertFilterValueDialog } from './UpsertFilterValueDialog';
import { FilterValue } from '../../interfaces/ProductFilter/FilterValue';
import { TranslationAdornment } from '../../components/Translation/TranslationAdornment';
import { LanguageDropdown } from '../../components/Dropdowns/LanguageDropdown';

export const ModifyFilterPage = () => {
  const navigate = useNavigate();

  const { filterID: filterIDAsStr } = useParams<string>();

  const filterID = filterIDAsStr ? Number(filterIDAsStr) : undefined;
  const isEditing = Number.isFinite(filterID);

  const [filterWithValues, setFilterWithValues] = useState(
    getEmptyFilterWithValues(),
  );
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [clickedValue, setClickedValue] = useState<FilterValue | undefined>(
    undefined,
  );
  const [selectedLocale, setSelectedLocale] = useState<string | null>(null);

  const refreshFilter = () => {
    setLoading(true);
    getFilterWithValues(filterID!)
      .then((f) => setFilterWithValues(f))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshFilter();
    }
  }, [filterIDAsStr]);

  const handleSave = () => {
    if (isEditing) {
      setLoading(true);
      updateFilterKey(filterWithValues)
        .then((updatedFilter) => {
          toast.success('Filtre başarıyla güncellendi');
          setFilterWithValues(updatedFilter);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createFilterKey(filterWithValues)
        .then((createdFilter) => {
          toast.success('Filtre başarıyla oluşturuldu');
          navigate(`/products/filters/${createdFilter.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteFilterKey(filterWithValues.id)
      .then(() => {
        toast.success('Filtre başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/products/filters');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili filtreyi silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      {(showCreateDialog || !!clickedValue) && (
        <UpsertFilterValueDialog
          open
          onUpsert={refreshFilter}
          onClose={() => {
            setShowCreateDialog(false);
            setClickedValue(undefined);
          }}
          keyID={filterID!}
          selectedLocale={selectedLocale}
          clickedValue={clickedValue}
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Filtreyi Düzenle' : 'Yeni Filtre'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 400,
          height: 'fit-content',
          p: 2.5,
          boxSizing: 'border-box',
          gap: 1.5,
        }}
      >
        <TextField
          label='İsim'
          variant='outlined'
          size='small'
          value={filterWithValues.title}
          onChange={(e) =>
            setFilterWithValues({ ...filterWithValues, title: e.target.value })
          }
          fullWidth
          InputProps={{
            endAdornment: (
              <TranslationAdornment
                defaultValue={filterWithValues.title}
                value={filterWithValues.titleTranslation}
                onChange={(x) =>
                  setFilterWithValues({
                    ...filterWithValues,
                    titleTranslation: x,
                  })
                }
              />
            ),
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={filterWithValues.isActive}
                onChange={() =>
                  setFilterWithValues({
                    ...filterWithValues,
                    isActive: !filterWithValues.isActive,
                  })
                }
              />
            }
            label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kaydet
          </Button>
        </Box>
      </Paper>
      {isEditing && (
        <Paper
          sx={{
            mt: 1.5,
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            boxSizing: 'border-box',
          }}
        >
          <Typography variant='h6'>Değerler</Typography>

          <Divider />

          <Box sx={{ width: 150 }}>
            <LanguageDropdown
              label='Dil'
              allowDefault
              value={selectedLocale}
              onChange={setSelectedLocale}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            {filterWithValues.values.map((filterValue) => (
              <FilterValueChip
                key={filterValue.id}
                keyID={filterID!}
                filterValue={filterValue}
                selectedLocale={selectedLocale}
                allowDelete={!selectedLocale}
                onModify={refreshFilter}
                onClick={() => setClickedValue(filterValue)}
              />
            ))}

            {!selectedLocale && (
              <Button
                color='primary'
                onClick={() => setShowCreateDialog(true)}
                sx={{ ...sharedStyles.buttonText }}
              >
                + Yeni Değer
              </Button>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};
